import { Slider } from "@/components/ui/slider";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ArrowsOut,
  Pause,
  PictureInPicture,
  Play,
} from "@phosphor-icons/react/dist/ssr";
import React, { useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player";
import screenfull from "screenfull"; // Import screenfull

export default function MediaVideo({
  className,
  url,
  hideControls,
  playing: propPlaying,
  onOpen,
  onClose,
  onPlay,
}) {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const [playing, setPlaying] = useState(propPlaying);
  const [played, setPlayed] = useState(0);
  const [duration, setDuration] = useState(0);
  const [pip, setPip] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    setIsClient(true); // Ensure this is only done on the client side
  }, []);

  // Update playing state when the prop changes
  useEffect(() => {
    setPlaying(propPlaying);
  }, [propPlaying]);

  useEffect(() => {
    const handleKeydown = (event) => {
      if (event.key === " " || event.keyCode === 32) {
        const activeElement = document.activeElement;
        const isTyping =
          activeElement?.tagName === "INPUT" ||
          activeElement?.tagName === "TEXTAREA" ||
          activeElement?.isContentEditable;

        if (!isTyping) {
          event.preventDefault();
          setPlaying((prev) => !prev);
        }
      }
    };

    document.addEventListener("keydown", handleKeydown);
    return () => {
      document.removeEventListener("keydown", handleKeydown);
    };
  }, []);

  //   useEffect(() => {
  //     const handleKeydown = (event) => {
  //       if (event.key === " " || event.keyCode === 32) {
  //         event.preventDefault();
  //         setPlaying((prev) => !prev);
  //       }
  //     };

  //     if (isFullscreen) {
  //       document.addEventListener("keydown", handleKeydown);
  //     }

  //     return () => {
  //       document.removeEventListener("keydown", handleKeydown);
  //     };
  //   }, [isFullscreen]);

  const handlePlayPause = () => {
    setPlaying((prev) => !prev);
  };

  const handleTogglePIP = () => {
    if (!pip) {
      videoRef.current?.getInternalPlayer()?.requestPictureInPicture();
    } else {
      document.exitPictureInPicture();
    }
  };

  const handleProgress = (state) => {
    setPlayed(state.played * 100);
  };

  const handleDuration = (dur) => {
    setDuration(dur);
  };

  const handleSeekChange = (value) => {
    const seekTo = value[0] / 100;
    videoRef.current.seekTo(seekTo, "fraction");
    setPlayed(value[0]);
  };

  const formatTime = (seconds) => {
    const min = Math.floor(seconds / 60);
    const sec = Math.floor(seconds % 60);
    return `${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  };

  const handleFullscreen = () => {
    if (screenfull.isEnabled && playerRef.current) {
      screenfull.toggle(playerRef.current);
    }
  };

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(screenfull.isFullscreen);

      // Call onClose when exiting fullscreen
      if (!screenfull.isFullscreen && onClose) {
        onClose();
      }
    };

    screenfull.on("change", onFullscreenChange);

    return () => {
      screenfull.off("change", onFullscreenChange);
    };
  }, [onClose]);

  // Trigger fullscreen and play when onPlay is invoked
  useEffect(() => {
    if (onPlay) {
      onPlay();
      handleFullscreen(); // Enter fullscreen mode
      setPlaying(true); // Start the video
    }
  }, [onPlay]);

  if (!isClient) {
    return null;
  }

  return (
    <div
      ref={playerRef}
      className={`relative group cursor-pointer overflow-hidden ${className}`}
    >
      <ReactPlayer
        ref={videoRef}
        width='100%'
        height='100%'
        url={url}
        playing={playing}
        onProgress={handleProgress}
        onDuration={handleDuration}
        stopOnUnmount={false}
        pip={pip}
        onEnablePIP={() => setPip(true)}
        onDisablePIP={() => setPip(false)}
      />
      <span onClick={handlePlayPause} className='absolute inset-0'></span>
      {!hideControls && (
        <div className='absolute inset-x-0 bottom-0 z-10 flex items-center gap-3 p-2 duration-500 translate-y-full bg-background/40 backdrop-blur group-hover:translate-y-0 transition-trasform'>
          <button onClick={handlePlayPause}>
            {playing ? (
              <Pause weight='fill' className='text-xl' />
            ) : (
              <Play weight='fill' className='text-xl' />
            )}
          </button>
          <Slider
            value={[played]}
            max={100}
            step={1}
            onValueChange={handleSeekChange}
          />
          <span className='flex-shrink-0 inline-block text-xs tracking-widest'>
            {formatTime((played / 100) * duration)} / {formatTime(duration)}
          </span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button onClick={handleTogglePIP}>
                  <PictureInPicture className='text-xl' />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Picture in Picture</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button onClick={handleFullscreen}>
                  <ArrowsOut className='text-xl' />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Fullscreen</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}
    </div>
  );
}
